import React, { useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { SeedCard } from 'components/customComponents/Layers/InputGroups';
import { ReactComponent as PlusCircle } from 'Icons/PlusCircle.svg';
import ELayersView from 'enums/ELayersView';
import { SeedLayerDTO } from 'business/models/CustomModels';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import { LayerDTO, SeedLayerDetailsDTO } from 'business/models/Models';
import {
  selectIsEditable,
  selectIsModifyAccessRights,
} from 'redux/selectors/StateSelectors';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import {
  selectExperimentLayers,
  selectSelectedLayerId,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { useAllowLayerEditing } from 'redux/hooks/experiment';

function SeedLayerContent({ setContentView, setEditSeedData, editSeedData }) {
  const dispatch = useRMDispatch();
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const modifyAccessRights: boolean = useRMSelector(selectIsModifyAccessRights);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const experimentLayers = useRMSelector(selectExperimentLayers);
  const setLayers = useCallback(
    (layers: LayerDTO[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const isLayerEditingAllowed = useAllowLayerEditing();

  function editSeedLayer() {
    const selectedLayer = experimentLayers.find((l) => l.id === _selectedLayerId);
    const seedLayerDTO: SeedLayerDTO = new SeedLayerDTO();
    seedLayerDTO.layerId = (selectedLayer.layerDetail as SeedLayerDTO).layerId;
    seedLayerDTO.seedDropVolumeVaryAlong = (
      selectedLayer.layerDetail as SeedLayerDTO
    ).seedDropVolumeVaryAlong;
    seedLayerDTO.startSeedDropVolume = (
      selectedLayer.layerDetail as SeedLayerDTO
    ).startSeedDropVolume;
    seedLayerDTO.stopSeedDropVolume = (
      selectedLayer.layerDetail as SeedLayerDTO
    ).stopSeedDropVolume;
    seedLayerDTO.seedId = (selectedLayer.layerDetail as SeedLayerDTO).seedId;
    seedLayerDTO.layerName = selectedLayer.name;

    setEditSeedData(seedLayerDTO);
    setContentView(ELayersView.EDITSEED);
  }

  function deleteSeedLayer() {
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((l) => l.id === _selectedLayerId);
    (selectedLayer.layerDetail as SeedLayerDTO).seedId = undefined;
    selectedLayer.dirty = true;
    callPreserveUndoableState();
    setLayers(layers);
  }

  function seedLayerExists() {
    const layers = [...experimentLayers];

    const seedLayers = layers.find((layer) => layer.id == _selectedLayerId);
    const seedLayerDetails: SeedLayerDetailsDTO =
      seedLayers.layerDetail as SeedLayerDetailsDTO;

    if (seedLayerDetails.seedId !== undefined) {
      return true;
    }

    return false;
  }

  function getSeedLayerView() {
    return (
      <SeedCard id={`commandpanel-seedlayercontent-layerview-seedcard`}
        onDeleteSeed={() => deleteSeedLayer()}
        onEditSeed={() => editSeedLayer()}
        layerData={experimentLayers.find((l) => l.id === _selectedLayerId)}
        editSeedData={editSeedData}
        isEditMode={isEditMode && modifyAccessRights && isLayerEditingAllowed}
      />
    );
  }

  function addIngredient() {
    setContentView(ELayersView.SELECTSEEDS);
  }

  return (
    <>
      {!seedLayerExists() && isEditMode && modifyAccessRights && (
        <Button
          id="commandpanel-seedinglayer-content-success-button"
          variant="success"
          className="add-ingredient-button"
          onClick={addIngredient}
        >
          <PlusCircle id='commandpanel-seedinglayer-content-add-seed' /> Add Seed
        </Button>
      )}
      {seedLayerExists() && (
        <div id='commandpanel-seedinglayer-content-getseedlayerview' className="h-[45vh] w-full overflow-y-auto overflow-x-hidden">
          {getSeedLayerView()}
        </div>
      )}
    </>
  );
}

export default SeedLayerContent;
